import { ElementRef, useCallback, useEffect, useRef, useState } from 'react';

import hostedFields, {
  HostedFieldsHostedFieldsFieldData,
  HostedFieldsHostedFieldsFieldName,
} from 'braintree-web/hosted-fields';
import { useTranslation } from 'react-i18next';
import threeDSecure from 'braintree-web/three-d-secure';
import { useErrorBoundary } from 'react-error-boundary';

import {
  BraintreeCardForm,
  getBillingAddress,
  getCardErrorMessage,
  // getVerifyCardFormattedAmount,
  createBraintreeCardFinalizeRequestBody,
} from '../utils';
import { sendFEEvent } from '../../../../../eventUtil';
import { useAPI } from '../../../../../services/api.service';
import { useCheckout } from '../../../../../hooks/use-checkout';
import { useOrderPolling } from '../../../hooks/use-order-polling';
import { usePaymentState } from '../../../hooks/use-payment-state';
import { braintreeService } from '../../../../../services/braintree.service';
import { AddressElementRef } from '../../AddressElement/hooks/use-address-element-ref';
import {
  ESdkEvents,
  EventParams,
  BraintreeFinalizePaymentResponse,
  AppchargeErrorType,
} from '../../../../../contracts';
import { generateAppchargeErrorMessage } from '../../../../../utils/generate-appcharge-error-message';

export function useBraintreeCard(onReady: () => void) {
  const { checkoutData, selectedPaymentMethod, getEventParams } = useCheckout();
  const { showBoundary } = useErrorBoundary();
  const setIsPaymentLoading = usePaymentState((state) => state.setIsPaymentLoading);
  const setErrorMessage = usePaymentState((state) => state.setErrorMessage);
  const fullAddress = usePaymentState((state) => state.fullAddress);
  const zipCode = usePaymentState((state) => state.zipCode);
  const { API } = useAPI();
  const { startOrderPolling } = useOrderPolling();
  const { t } = useTranslation(undefined, { keyPrefix: 'checkout' });
  const [isAvailable, setIsAvailable] = useState(true);

  const isBraintreePaymentInProgress = useRef(false);
  const cardNumberRef = useRef<ElementRef<'div'>>();
  const securityCodeRef = useRef<ElementRef<'div'>>();
  const expirationDateRef = useRef<ElementRef<'div'>>();
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);
  const [hostedFieldsInstance, setHostedFieldsInstance] = useState<hostedFields.HostedFields>();
  const [threeDSecureInstance, setThreeDSecureInstance] = useState<threeDSecure.ThreeDSecure>();
  const [cardNumberMetadata, setCardNumberMetadata] = useState({
    message: '',
    isValid: false,
    ref: cardNumberRef,
  });
  const [securityCodeMetadata, setSecurityCodeMetadata] = useState({
    message: '',
    isValid: false,
    ref: securityCodeRef,
  });
  const [expirationDateMetadata, setExpirationDateMetadata] = useState({
    message: '',
    isValid: false,
    ref: expirationDateRef,
  });

  const braintreePK = checkoutData?.pks.braintree;

  const validateHostedFields = useCallback(
    (field: string, meta: HostedFieldsHostedFieldsFieldData) => {
      const newFieldState = { message: getCardErrorMessage(field, meta), isValid: meta.isValid };
      switch (field) {
        case 'number':
          setCardNumberMetadata((prevState) => ({
            ...prevState,
            ...newFieldState,
          }));
          break;
        case 'expirationDate':
          setExpirationDateMetadata((prevState) => ({
            ...prevState,
            ...newFieldState,
          }));
          break;
        case 'cvv':
          setSecurityCodeMetadata((prevState) => ({
            ...prevState,
            ...newFieldState,
          }));
          break;
        default:
          console.error('unsupported field');
          break;
      }
    },
    []
  );

  const moveToNextField = useCallback(
    (field: string, hostedFieldsInstance: hostedFields.HostedFields) => {
      switch (field) {
        case 'number':
          hostedFieldsInstance?.focus('expirationDate');
          break;
        case 'expirationDate':
          hostedFieldsInstance?.focus('cvv');
          break;
      }
    },
    []
  );

  const initCardFields = useCallback(
    async (braintreePK: string) => {
      try {
        onReady();
        const clientInstance = await braintreeService.createClientInstance(braintreePK);
        const createdHostedFieldsInstancePromise = braintreeService.createHostedFields({
          clientInstance,
          cardNumberContainer: cardNumberRef.current!,
          securityCodeContainer: securityCodeRef.current!,
          expirationDateContainer: expirationDateRef.current!,
        });
        // const threeDSecurePromise = braintreeService.createThreeDSecure(clientInstance);
        const [createdHostedFieldsInstance] = await Promise.all([
          createdHostedFieldsInstancePromise,
          // threeDSecurePromise,
        ]);
        setIsAvailable(true);
        setHostedFieldsInstance(createdHostedFieldsInstance);
        // setThreeDSecureInstance(threeDSecure);
        // onReady();
      } catch (error) {
        setIsAvailable(false);
        console.error(error);
      }
    },
    [onReady]
  );

  useEffect(() => {
    initCardFields(braintreePK || '');
  }, [braintreePK, initCardFields]);

  useEffect(() => {
    if (hostedFieldsInstance) {
      hostedFieldsInstance.on('blur', ({ emittedBy, fields }) => {
        validateHostedFields(emittedBy, fields[emittedBy]);
      });

      hostedFieldsInstance.on('validityChange', ({ emittedBy, fields }) => {
        const fieldMeta = fields[emittedBy];
        validateHostedFields(emittedBy, fieldMeta);
        if (fieldMeta.isValid) {
          moveToNextField(emittedBy, hostedFieldsInstance);
        }
      });
    }
  }, [hostedFieldsInstance]);

  const handleSubmit = async (
    formValues: BraintreeCardForm,
    isFormValid: boolean,
    addressComponent?: AddressElementRef
  ) => {
    const isZipCodeError = addressComponent && !addressComponent.isValid();

    if (!hostedFieldsInstance) {
      if (isFormValid && !isZipCodeError && !isBraintreePaymentInProgress.current) {
        setErrorMessage(t('appchargeErrors.generic'));
      }
      return;
    }

    const fieldsState = hostedFieldsInstance.getState();
    let isHostedFieldsValid = true;
    Object.entries(fieldsState.fields).forEach(([field, meta]) => {
      isHostedFieldsValid = isHostedFieldsValid && meta.isValid;
      validateHostedFields(field, meta);
    });

    if (
      isHostedFieldsValid &&
      isFormValid &&
      !isZipCodeError &&
      !isBraintreePaymentInProgress.current
    ) {
      toggleHostedFields(true);
      isBraintreePaymentInProgress.current = true;
      setIsPaymentLoading(true);
      try {
        const billingAddress = getBillingAddress(
          addressComponent?.values.zipCode || zipCode,
          addressComponent?.values.address || fullAddress,
          checkoutData?.customer
        );
        const tokenizationResults = await hostedFieldsInstance.tokenize({
          billingAddress,
        });

        if (!tokenizationResults?.nonce) {
          handleError();
          return;
        }

        // threeDSecureInstance?.on('lookup-complete', (_, next) => {
        //   next?.();
        // });
        // const formattedAmount = getVerifyCardFormattedAmount(priceBreakdown?.priceTotal as number);
        // const threeDSecureResults = await threeDSecureInstance?.verifyCard({
        //   nonce: tokenizationResults?.nonce!,
        //   bin: tokenizationResults?.details.bin!,
        //   amount: formattedAmount,
        // });
        const FEeventParams: Partial<EventParams> = {
          isNewPaymentMethodSaved: formValues.shouldSaveCard,
        };
        const finalizePaymentRequestBody = createBraintreeCardFinalizeRequestBody(
          tokenizationResults.nonce,
          formValues,
          selectedPaymentMethod!,
          tokenizationResults.details
        );

        const finalizePaymentResponse = await API.finalizePayment<BraintreeFinalizePaymentResponse>(
          finalizePaymentRequestBody
        );
        handleServerResponse(finalizePaymentResponse, FEeventParams);
      } catch (error: any) {
        const errResponse = error?.response?.data;
        setIsPaymentLoading(false);
        showBoundary(t('unableToProcessPayment'));
        sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
          ...getEventParams(),
          reason: errResponse.message,
          error: {
            isFatal: true,
            reason: errResponse.message,
          },
        });
      }
    }
  };

  const handleServerResponse = (
    finalizePaymentResponse: BraintreeFinalizePaymentResponse,
    FEeventParams: Partial<EventParams>
  ) => {
    const { status, message } = finalizePaymentResponse.braintree;
    if (status === 'succeeded') {
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_SUCCESS, {
        ...getEventParams(),
        ...FEeventParams,
      });
      startOrderPolling(checkoutData?.orderId!, FEeventParams);
    } else if (status === 'failed') {
      handleError(finalizePaymentResponse.appChargeErrorType);
      sendFEEvent(ESdkEvents.PAYMENT_INTENT_FAILED, {
        ...getEventParams(),
        ...FEeventParams,
        reason: message,
      });
    }
  };

  const handleError = (appChargeErrorType?: AppchargeErrorType) => {
    const errorMessage = generateAppchargeErrorMessage(t, appChargeErrorType);
    isBraintreePaymentInProgress.current = false;
    setErrorMessage(errorMessage);
    setIsPaymentLoading(false);
    toggleHostedFields(false);
  };

  const toggleHostedFields = (shouldDisable: boolean) => {
    const fields: HostedFieldsHostedFieldsFieldName[] = ['cvv', 'number', 'expirationDate'];
    fields.forEach((field) =>
      shouldDisable
        ? hostedFieldsInstance?.setAttribute({ field, attribute: 'disabled' })
        : hostedFieldsInstance?.removeAttribute({ field, attribute: 'disabled' })
    );
  };

  return {
    cardNumberMetadata,
    securityCodeMetadata,
    expirationDateMetadata,
    handleSubmit,
    isAvailable,
  };
}
